import { useImperativeHandle, useRef } from 'react';
import { Button, Checkbox, ControlLabel, Dialog, DialogContent, DialogRef, Divider, HelperText, Radio, Stack, Typography } from 'src/app/components';
import { HookFormProvider, InputDatePicker, InputTextField, InputTextEditor, InputImageUpload, InputRadioGroup, InputTimePicker, InputSelectAsync, InputSelect, InputFileUpload } from 'src/app/components/HookForm';
import InputSelectMall from 'src/app/components/HookForm/InputSelect/InputSelect.mall';
import { DateObject } from 'react-multi-date-picker';
import { DIGITAL, FISIK, PARKING, PRODUCT, SalesPromoPrizeFormModel, useSalesPromoAddPrizeForm, VALET } from './SalesPromoAddPrizePopup.hook';
import { Controller } from 'react-hook-form';
import SelectAllCheckbox from 'src/app/components/Inputs/SelectAllCheckbox/SelectAllCheckbox.page';
import { isEmpty } from 'lodash';
import { getStoreCategory } from 'src/app/modules/directory/services/StoreCategory.service';
import BankRewardCodesDialog from 'src/app/modules/rewards/page/Rewards/BankRewardsDialog/BankRewardsDialog.page';
import BankRewardValidateDialog from 'src/app/modules/rewards/page/Rewards/BankRewardsDialog/BankRewardValidateDialog.page';
import { validateQuota } from 'src/app/modules/rewards/page/Rewards/RewardsForm/RewardsForm.utils';
import { SalesPromoPrizeModel } from 'src/app/modules/campaign/models/SalesPromo.model';
import { LoadingIcon } from 'src/app/components/Icons';
import { twMerge } from 'tailwind-merge';
import { Theme } from 'src/app/components/HookForm/HookForm/HookForm.theme';
export interface SalesPromoAddPrizePopupProps {
  dialogRef?: React.RefObject<DialogRef>;
  submit?: () => void;
  itemToEdit?: { id?: string, type?: 'rewards' | 'product', status?: string, data?: SalesPromoPrizeModel };
  [key: string]: any; // <-- allows any other props (optional)
}
const typeFieldMap: Record<string, string[]> = {
  PARKING,
  VALET,
  DIGITAL,
  FISIK,
  PRODUCT,
};

export type SalesPromoAddPrizePayload = {
  type?: keyof typeof typeFieldMap;
  [key: string]: any; // allow dynamic string keys
};

const SalesPromoAddPrizePopup = ({ dialogRef, submit, cancel, ...props }: SalesPromoAddPrizePopupProps) => {
  const state = useSalesPromoAddPrizeForm(props);

  const handleSubmit = state.form.handleSubmit(
    async ({
      reward_type,
      merchant_categories,
      merchants,
      directories,
      directory,
      bank_rewards,
      image,
      ...formData
    }) => {
      // Prepare the bank_reward_codes array with only the 'code' values from csvData
      const bank_reward_codes = state.state.csvData.map(item => item.code)
      const directoriesIds = directories?.map((item) => {
        return (item.id as string)
      })

      // Use array with one id if form Mall Type is not multiple
      const directory_ids = ['DIGITAL', 'FISIK'].includes(state.state.valPrizeType as string) ? directoriesIds : [directory?.id as string]

      // Create the final payload, includes only neccesary keys
      const payload: SalesPromoAddPrizePayload = {
        type: reward_type?.value,
        merchant_category_ids: merchant_categories?.map((_) => _?.value || _?.id),
        merchant_ids: merchants?.map((_) => _?.value || _?.id),
        directory_ids,
        bank_reward_codes, // include only the codes from csvData
        image: image?.split('base64,', 2)[1],
        id: formData.id,
        name: formData.name,
        is_bank_reward: formData.is_bank_reward,
        description_idn: formData.description_idn,
        description_eng: formData.description_eng,
        quantity: formData.quantity,
        days: formData.is_bank_reward ? [] : formData.days,
        validity_period_days: formData.validity_period_days,
        valid_until: formData.valid_until,
        discount_amount: formData.discount_amount,
        reward_start_time: formData.reward_start_time,
        reward_end_time: formData.reward_end_time,
      }

      const filteredPayload = Object.keys(payload).reduce((acc, key) => {
        const allowedFields = typeFieldMap[payload.type ?? ''];
        if (allowedFields?.includes(key)) {
          acc[key] = payload[key];
        }
        return acc;
      }, {} as Record<string, any>);

      hitPostAPI(filteredPayload);
    }
  )

  const hitPostAPI = async (payload: SalesPromoPrizeFormModel) => {
    try {
      await state.submit.mutateAsync(payload)
      clickCancel()
    } catch (error: any) {
      errorPostApi(error)
    }
  }

  const errorPostApi = (err?: any) => {
    if (err?.response?.data?.response_schema?.response_code.includes('400')) {
      state.form.setError('name', {
        type: 'required',
        message: 'Prizes name already exist in this mall, please enter another name or change draw location.',
      })
      scrollToTop()
    }
  }

  const scrollToTop = () => {
    const modalWrapper = document.getElementById('dialog-wrapper-content');
    if (modalWrapper) {
      const modalBody = modalWrapper.querySelector('.overflow-y-scroll');
      if (modalBody) {
        modalBody.scrollTo({
          top: modalWrapper.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  }

  const localRef = useRef<DialogRef>(null);

  function clickCancel() {
    state.form.reset();
    state.setCsvData([])
    state.setIsEdit(false)
    state.setSalesPromoDetailStatus('')
    props.itemToEdit = {}
    cancel && cancel();
    localRef.current?.hide();
  }

  useImperativeHandle(dialogRef, () => ({
    show: () => localRef.current?.show(),
    hide: () => localRef.current?.hide(),
  }), []);


  const renderDayItem = (item: string) => {
    const value = item.toUpperCase()
    const isChecked = state.state.days.includes(value)
    return (
      <Checkbox
        disabled={false}
        checked={isChecked}
        label={item}
        value={value}
        key={item}
        onChange={state.action.handleChangeDay}
      />
    )
  }

  const onDeleteDialog = (index: number) => {
    const updatedCodes = state.state.valBankRewardCodes.filter((_, i) => i !== index)
    state.form.setValue('quantity', updatedCodes?.length)
    state.form.setValue('bank_reward_codes', updatedCodes)
  }

  const errors = state.form.formState.errors

  return (
    <>
      <Dialog
        closeOnBackdropClicked={false}
        title={(state.isEdit ? 'Edit' : 'Add New') + " Prize"}
        size="md"
        ref={localRef}
        onHide={clickCancel}
      >
        <DialogContent className="bg-white flex flex-col">
          <>
            {(state.detailPrizeItem.isLoading || state.detailPrizeItem.isFetching) && state.isEdit && <div className={twMerge(Theme.mask)}>
              <LoadingIcon />
            </div>}
            <HookFormProvider
              enables={state.enabledFields()}
              form={state.form}
            >
              <Stack className="p-6">
                <ControlLabel label="Prizes Type" required={true}>
                  <InputSelect
                    placeholder="Select prize type"
                    isMulti={false}
                    options={state.prizeTypes}
                    rule={{ required: true }}
                    form={state.form}
                    name="reward_type"
                    selectOption={(_) => ({
                      label: _.label,
                      value: _.value,
                    })}
                    onChangeValue={state.action.rewardTypeOnChange}
                  />
                </ControlLabel>
                <ControlLabel label="Prizes Name">
                  <InputTextField
                    placeholder="Enter prizes name"
                    rule={{ required: true }}
                    name="name"
                    maxLength={120}
                  />
                </ControlLabel>
                <ControlLabel label={state.state.valPrizeType === 'PRODUCT' ? 'Prize Location' : 'Mall'}>
                  <InputSelectMall placeholder={state.state.valPrizeType === 'PRODUCT' ? 'Select location' : 'Select mall'} isMulti={['DIGITAL', 'FISIK'].includes(state.state.valPrizeType as string)} form={state.form} rule={{ required: true }} onChangeValue={state.action.mallOnChange} />
                </ControlLabel>
                {(['DIGITAL', 'FISIK'].includes(state.state.valPrizeType as string)) && <>
                  <SelectAllCheckbox
                    label="Select All Store Category"
                    onChecked={(selected) => {
                      state.form.setValue('merchant_categories', state?.storeCategoryList?.data?.content!)
                      state.form.setValue('merchants', state?.store?.data?.content!)
                    }}
                    onUnChecked={() => {
                      state.form.setValue('merchant_categories', [])
                      state.form.setValue('merchants', [])
                    }}
                    checked={
                      (state.state.storeCategories.length === state.storeCategoryList.data?.content?.length) &&
                      (state.state.stores.length === state.store.data?.content?.length)
                    }
                    options={state.storeCategoryList.data?.content}
                    selectOption={(_) => ({
                      label: _.category_name!,
                      value: _.id!,
                    })}
                    disabled={isEmpty(state.state.directories)}
                  />
                  <ControlLabel label="Category">
                    <InputSelectAsync
                      placeholder="Select store category"
                      disabled={!state.state.directories || state.state.directories.length < 1}
                      fetchApi={getStoreCategory}
                      payload={{ directory_ids: state.state.directories, statuses: ['ACTIVE'] }}
                      onChangeValue={state.action.handleSyncStoreCategoryReward}
                      isMulti={true}
                      rule={{ required: true }}
                      form={state.form}
                      name="merchant_categories"
                      selectOption={(_) => ({
                        label: _.category_name!,
                        value: _.id!,
                      })}
                    />
                  </ControlLabel>
                  <ControlLabel label="Store">
                    <InputSelect
                      placeholder="Select store"
                      disabled={!state.state.storeCategories || state.state.storeCategories.length < 1}
                      options={state.store.data?.content}
                      onChangeValue={state.action.handleSyncStore}
                      isMulti={true}
                      required={true}
                      form={state.form}
                      name="merchants"
                      selectOption={(_) => ({
                        label: _.merchant_name!,
                        value: _.id!,
                      })}
                    />
                  </ControlLabel>
                </>}
                {state.state.valPrizeType === 'DIGITAL' && <>
                  <ControlLabel label="Voucher from Tenant" required={true}>
                    <InputRadioGroup
                      form={state.form}
                      name="is_bank_reward"
                      rule={{ required: true }}
                      onChange={() => {
                        state.form.setValue('bank_reward_codes', undefined)
                        state.form.control._formState.errors.merchants = undefined
                        state.form.control._formState.errors.merchant_categories = undefined
                      }}
                    >
                      <Radio key={'is_bank_reward_true'} label="Yes" value={'true'} />
                      <Radio key={'is_bank_reward_false'} label="No" value={'false'} />
                    </InputRadioGroup>
                    {state.state.isVoucherFromTenant && (<>
                      <div className="flex gap-1 justify-end items-center">
                        <svg width="17" height="17" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.1009 12.8633L10.1009 2.8291" stroke="#7C5BFA" stroke-width="1.5"
                            stroke-linecap="round" stroke-linejoin="round" />
                          <path d="M12.5312 10.4233L10.1013 12.8633L7.67125 10.4233" stroke="#7C5BFA" stroke-width="1.5"
                            stroke-linecap="round" stroke-linejoin="round" />
                          <path
                            d="M13.9611 6.77295H14.7386C16.4344 6.77295 17.8086 8.14712 17.8086 9.84378V13.9138C17.8086 15.6055 16.4378 16.9763 14.7461 16.9763H5.46276C3.76693 16.9763 2.39193 15.6013 2.39193 13.9055L2.39193 9.83462C2.39193 8.14378 3.76359 6.77295 5.45443 6.77295L6.23943 6.77295"
                            stroke="#7C5BFA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <a
                          href="https://pakuwon-be.sandbait.work/v1/aws/rewards/bank_reward_template.csv/download"
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 text-xs text-right"
                        >
                          Download Voucher Template
                        </a>
                        <div className="group relative">
                          <svg width="14" height="14" viewBox="0 0 18 18" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M9 4.84167V4.83335M9 13.1667L9 7.33333M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
                              stroke="#667085" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <div
                            className="absolute bottom-full -right-4 mb-2 hidden group-hover:block bg-white text-gray-700 text-sm rounded-md shadow-lg p-4 w-72 z-10">
                            <div className="font-semibold mb-2">How to use</div>
                            <ol className="list-decimal list-inside text-xs text-gray-500">
                              <li>Download the voucher template</li>
                              <li>Fill in the file with your unique voucher code</li>
                              <li>Upload your filled template</li>
                            </ol>
                            <div className="absolute bottom-[-6px] right-4 w-3 h-3 bg-white transform rotate-45"></div>
                          </div>
                        </div>
                      </div>
                    </>
                    )}
                  </ControlLabel>
                  {state.state.isVoucherFromTenant && (
                    <>
                      <div className="flex justify-end">
                        <div className="w-4/6">
                          <InputFileUpload
                            className="w-full mb-4"
                            maxSize={Math.pow(1024, 2) * 5}
                            name="bank_reward_codes_file"
                            form={state.form}
                            type="CSV"
                            rule={{
                              validate: (event) => {
                                if (event && event.filename) {
                                  return (event.filename.split('.').pop() === 'csv') || 'Wrong file format'
                                }
                                return (state.state.valBankRewardCodes.length > 0 || state.state.valBankRewards.length > 0) || 'Field cannot be empty'
                              }
                            }}
                            onChangeFile={(e) => state.action.onChangeUploadBankRewardCodes(e)}
                          />
                          {state.state.csvData.length > 0 && (
                            <Button variant="outlined" color="disabled" className="text-black" onClick={() => {
                              state.state.bankRewardDialogRef.current?.show()
                            }}>
                              View Voucher Code List
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>}
                {(!['DIGITAL', 'FISIK', 'PRODUCT'].includes(state.state.valPrizeType as string)) && <>
                  <ControlLabel label="Discount Amount" required={true}>
                    <InputTextField
                      placeholder="000.000.000"
                      helperText={!errors.discount_amount ? 'This voucher is worth of Rp x' : undefined}
                      rule={{ required: true }}
                      name="discount_amount"
                      type="currency"
                    />
                  </ControlLabel>
                </>}
                <ControlLabel label="Description (EN)">
                  <div>
                    <InputTextEditor height={150} form={state.form} name="description_eng" />
                  </div>
                </ControlLabel>
                <ControlLabel label="Description (ID)">
                  <div>
                    <InputTextEditor height={150} form={state.form} name="description_idn" />
                  </div>
                </ControlLabel>
                <ControlLabel label="Image" required={true}>
                  <div className="grid grid-cols-2 gap-8">
                    <InputImageUpload
                      placeholder="Upload Photo (JPG/PNG)"
                      maxSize={Math.pow(1024, 2) * 5}
                      accept={['.jpg', '.jpeg', '.png']}
                      rule={{ required: true }}
                      form={state.form}
                      name="image"
                    />
                    <div className="flex flex-col justify-center">
                      <ul className="text-xs text-gray-500 list-disc list-inside dark:text-gray-400">
                        <li className="mb-4">
                          Size: Max size of file is <strong>5 MB</strong>.
                        </li>
                        <li className="mb-4">
                          Photo ratio is <strong>16/9</strong>
                        </li>
                        <li>
                          File format: only allow <strong>.jpg / .png</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </ControlLabel>
                <ControlLabel label="Prizes Qty" required={true}>
                  <InputTextField
                    placeholder="Enter prizes quantity"
                    name="quantity"
                    type="number"
                    rule={{
                      validate: validateQuota(
                        state.form.watch('quantity'),
                        state.detailPrizeItem.data?.quantity,
                        state.detailPrizeItem.data?.quantity_remaining,
                        state.detailPrizeItem.data?.status === 'PENDING',
                      ),
                      required: true,
                      min: {
                        value: 1,
                        message: 'Minimal qty is 1',
                      },
                    }}
                    disabled={state.state.isVoucherFromTenant}
                  />
                </ControlLabel>
                {(!['FISIK', 'PRODUCT'].includes(state.state.valPrizeType as string)) && <>
                  <Typography variant="subtitle2">Usage Requirement</Typography>
                  {!state.state.isVoucherFromTenant && (<Controller
                    name="days"
                    control={state.form.control}
                    rules={{
                      required: 'Field cannot be empty',
                    }}
                    render={() => (
                      <ControlLabel label="Usable Day" required={true}>
                        <div>
                          <div className="grid grid-cols-4 gap-4">
                            {state.master.days.map(renderDayItem)}
                          </div>
                          <HelperText error={!!state.form.formState.errors.days}>
                            {state.form.formState.errors.days?.message}
                          </HelperText>
                        </div>
                      </ControlLabel>
                    )}
                  />)}
                  {state.state.valPrizeType !== 'DIGITAL' && <>
                    <ControlLabel label="Usable Hour" required={true}>
                      <Stack direction="row" items="center">
                        <InputTimePicker
                          placeholder="Select start hour"
                          className="flex-1"
                          rule={{ required: true }}
                          name="reward_start_time"
                        />
                        <Divider className="w-[24px] border-t-2" />
                        <InputTimePicker
                          placeholder="Select end hour"
                          className="flex-1"
                          rule={{ required: true }}
                          name="reward_end_time"
                        />
                      </Stack>
                    </ControlLabel>
                  </>}
                  <ControlLabel label="Max days of use after claimed" required={true}>
                    <InputTextField
                      placeholder="Enter max days"
                      name="validity_period_days"
                      type="number"
                      rule={{
                        required: true,
                        min: {
                          value: 1,
                          message: 'Minimal days is 1',
                        },
                        max: {
                          value: 999,
                          message: 'Maximal days is 999',
                        },
                      }}
                    />
                  </ControlLabel>
                  <ControlLabel label="Valid Until" required={true}>
                    <InputDatePicker
                      placeholder="Select valid date"
                      rule={{ required: true }}
                      name="valid_until"
                      minDate={new DateObject(props.currentDetail.end_date).add(1, 'day')}
                      form={state.form}
                    />
                  </ControlLabel>
                </>}
              </Stack>
              <Stack
                className={'border-t pt-4 border-neutral-200'}
                direction="row"
                gap={4}
              >
                <Button variant='outlined' onClick={clickCancel}>Cancel</Button>
                <Stack
                  direction="row"
                  justify='end'
                  gap={4}
                >
                  <Button onClick={() => handleSubmit()} loading={state.submit.isLoading}>{state.isEdit ? 'Save' : 'Add'}</Button>
                </Stack>
              </Stack>
            </HookFormProvider>
          </>
        </DialogContent>
      </Dialog>
      <BankRewardCodesDialog
        dialogRef={state.state.bankRewardDialogRef}
        bank_reward_codes={state.state.valBankRewardCodes ?? state.state.valBankRewards}
        onDelete={onDeleteDialog}
      />
      <BankRewardValidateDialog
        dialogRef={state.state.bankRewardValidateDialogRef}
        errors={state.state.errors}
        isLoadingParse={state.state.isLoadingParse}
        isLoadingValidate={state.state.isLoadingValidate}
        handleClickProceed={state.action.handleValidateBankRewardCodes}
      />
    </>
  );
};

export default SalesPromoAddPrizePopup;
